import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAfter, isBefore } from 'date-fns';
import { Service } from './Service';
import { ManageAccount } from './ManageAccount';
import {
  // Account,
  CtvAccount,
  InternetAccount,
  IptvAccount,
  IUserConnectedServices,
  UnionAccount,
  ServiceShare,
} from '../../../models/Accounts';
import { ServiceCardSkeleton } from '../../commons/ServiceCardSkeleton';
import { TextButton, TextButtonClassNames } from '../../commons/buttons/TextButton';
import {
  brizTvShareId,
  gigabitShareId,
  gigabitShareStartDate,
  gigabitShareStopDate,
  suspensionOfServicesIds,
  toDay,
} from '../../../constants/configuration';
import DateVO from '../../../core/valueObjects/DateVO';
import { useFindShare } from '../../../hooks/useFindShare';
import { useUserAddress } from '../../../hooks/useUserAddress';
import { useCustomSnackbar } from '../../../hooks/useCustomEnqueueSnackbar';
import { useAppSelector, useAppDispatch } from '../../../hooks/redux';
import { paymentsApi, ServicePresetsAllResponse } from '../../../services/apiServices/user/payments';
import { authorizationApi } from '../../../services/apiServices/user/authorization';
import { userInfoApi } from '../../../services/apiServices/user/userInfo';
import { initialState, userInfoSlice } from '../../../store/reducers/UserInfoSlice';
import { authSlice } from '../../../store/reducers/AuthSlice';
import { modalSlice } from '../../../store/reducers/ModalSlice';
import { ApiError } from '../../../models/ApiError';
import { ModalVariants, LocalStorageItems } from '../../../models/Common';
import { CtvPreset, InternetPreset, IptvPreset, ServicePreset, UnionPreset } from '../../../models/ServicePresets';
import { UnionServicesExIds } from '../../../models/Calculate';
import { GigabitSpeedPromo } from '../GigabitSpeedPromo';
import { NewOffersPromo } from '../NewOffersPromo';
import { TechnicalWorks } from '../TechnicalWorks';
import { getErrorMessage, isRepairWorkStarted } from '../../../utils';

interface ConnectedServices {
  type: string;
  title: string;
  price: number;
  isPriceDaily: boolean;
  isDisabled: boolean;
  expireDate: Date;
  id: number;
  status: string;
  statusId: number;
  login?: string;
  dedicatedIP?: string;
  isBrizTvShare?: boolean;
  brizTvShareActiveTo?: string;
  brizTvShareName?: string;
  films?: boolean;
  isBrizTvLogin?: boolean | null;
  serviceTypeID?: number;
  serviceAnalyticsName: string;
  shareAction: ServiceShare | null | undefined;
  isServiceActivationAvailable: boolean;
  servicePreset: ServicePreset | undefined;
  isSuspense: boolean;
  Hidden: boolean;
  transitionToBrizTVServiceId: number | undefined;
  isUnionCtvClassic: boolean;
  isInitLoading: boolean;
  serviceExIds: number[] | UnionServicesExIds | [];
  currentServiceId: number;
}

interface ConnectedAccounts {
  connectedAccounts: IUserConnectedServices | undefined;
  isLoadingConnectedAccounts: boolean | undefined;
}

export const ServiceCards: FC<ConnectedAccounts> = ({ connectedAccounts, isLoadingConnectedAccounts }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const { logout } = authSlice.actions;
  const [logoutApi] = authorizationApi.useLogoutMutation();
  const { resetUserInfoAfterLogout } = userInfoSlice.actions;
  const [connectedServices, setConnectedServices] = useState<ConnectedServices[]>([]);
  const controlledAccount = localStorage.getItem(LocalStorageItems.ControlledAccount);
  const { useGetUserInfoQuery, useGetUserInfoMultiAccQuery } = userInfoApi;
  const { data: userInfoState, isSuccess: isUserInfoDownloaded } = controlledAccount
    ? useGetUserInfoMultiAccQuery({ subAccountId: JSON.parse(controlledAccount).UserID })
    : useGetUserInfoQuery('');
  const { data: bannersInfo } = userInfoApi.useGetUserBannersQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const { data: repairWorksInfo } = userInfoApi.useGetUserRepairWorksQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const { selectedLanguage } = useAppSelector((state) => state.langReducer);
  const { settings } = useAppSelector((state) => state.appReducer);
  const { userAddress } = useUserAddress(userInfoState || initialState.userInfoState, selectedLanguage);
  const { share: brizTvShare } = useFindShare(brizTvShareId);
  const {
    data: servicePresetsAll,
    refetch,
    isLoading,
  } = paymentsApi.useGetServicePresetsAllWithTransformResponseQuery();

  const {
    data: accountInfo,
    isError: isAccountError,
    error: accountError,
  } = userInfoApi.useGetMultiaccountMembersQuery(selectedLanguage, {
    refetchOnMountOrArgChange: true,
    skip: !settings?.feature?.multiaccount,
  });
  const isServiceActivationAvailable = isUserInfoDownloaded && userInfoState.Permissions.Services.Activate;
  const internetGponAccounts = connectedAccounts?.internet_accounts?.filter((item) => item.Access.TypeID === 9) ?? [];
  const unionGponAccounts =
    connectedAccounts?.union_accounts?.filter((item) => item.Internet?.Access.TypeID === 9) ?? [];
  const isConnectedGpon = !!(internetGponAccounts.length || unionGponAccounts.length);
  const isGigabitActivated = !!userInfoState?.Shares.Activated.find((item) => item.id === gigabitShareId);
  const isGigabitSharePeriod =
    isAfter(toDay, DateVO.create(gigabitShareStartDate).get(true)) &&
    isBefore(toDay, DateVO.create(gigabitShareStopDate).get(true));
  const isShownGigabitSpeedPromo = isConnectedGpon && !isGigabitActivated && isGigabitSharePeriod;
  const showServiceCardsSkeleton =
    isLoadingConnectedAccounts || !isUserInfoDownloaded || !servicePresetsAll || !connectedServices.length;

  function getServicePresetById(
    services: ServicePresetsAllResponse | undefined,
    id: number,
  ): IptvPreset | UnionPreset | InternetPreset | CtvPreset | undefined {
    const matchingService =
      services &&
      Object.values(services)
        .filter((serviceList) => serviceList != null)
        .flatMap((serviceList) => serviceList)
        .find((service) => service.AccountID === id);
    return matchingService ?? undefined;
  }

  const handleSelectAnotherAccount = () => {
    dispatch(
      openModal({
        modalVariants: ModalVariants.SelectAccount,
        content: {
          accountId: userInfoState?.id,
        },
      }),
    );
  };

  const onLogout = async () => {
    try {
      await logoutApi('').unwrap();
      localStorage.removeItem(LocalStorageItems.ControlledAccount);
      dispatch(resetUserInfoAfterLogout());
      dispatch(logout());
    } catch (err) {
      const error = err as ApiError;
      if (error.status !== 403) {
        const message = getErrorMessage(err);
        customEnqueueSnackbar(message, 'error');
      }
    }
  };

  useEffect(() => {
    setConnectedServices([]);
  }, [userInfoState?.id]);

  useEffect(() => {
    refetch();
  }, [controlledAccount, refetch]);

  useEffect(() => {
    if (
      typeof accountInfo?.Account !== 'undefined' &&
      accountInfo?.Account?.Role !== 'member' &&
      controlledAccount &&
      !accountInfo.List.find((el) => el.UserID === JSON.parse(controlledAccount).UserID)
    ) {
      onLogout();
    }
  }, [accountInfo, controlledAccount]);

  useEffect(() => {
    if (isAccountError && accountError) {
      const message = getErrorMessage(accountError);
      customEnqueueSnackbar(message, 'error');
    }
  }, [isAccountError, accountError]);

  useEffect(() => {
    /** Ниже идет проверка подключен ли пользователь к акции BRIZ.TV Premium 14 дней */
    const brizTvShareName = brizTvShare && brizTvShare.Name;
    const brizTvShareActivateTo = brizTvShare && brizTvShare.ActiveTo;
    const isBrizTvShareActive =
      !!brizTvShareActivateTo && isAfter(DateVO.create(brizTvShareActivateTo.toString()).get(), toDay);

    if (connectedAccounts && isUserInfoDownloaded && !isLoadingConnectedAccounts) {
      setConnectedServices([]);

      Object.entries(connectedAccounts).map((el) => {
        return (
          el[1]?.length &&
          el[1].forEach((item: IptvAccount | UnionAccount | CtvAccount | InternetAccount) => {
            const servicePreset = getServicePresetById(servicePresetsAll, item.id);
            if (!servicePreset) return;
            const isInternetAccount = 'Access' in item && 'DedicatedIP' in item;
            const isIptvAccount = 'BrizTV' in item;
            const isUnionAccount = 'Internet' in item;
            const isSuspense = suspensionOfServicesIds.includes(item?.Service?.id);

            const isInternetDedicatedIP = isInternetAccount && !!item.DedicatedIP;
            const isUnionDedicatedIP = isUnionAccount && !!item.Internet?.DedicatedIP;
            const isUnionCtvClassic = isUnionAccount && !!item.Ctv && item.Ctv?.Access?.TypeID !== 9;

            const isIptvBrizTvLogin = isIptvAccount && (!!item.BrizTV?.Email || !!item.BrizTV?.Phone);
            const isUnionBrizTvLogin = isUnionAccount && (!!item.Iptv?.BrizTV?.Email || !!item.Iptv?.BrizTV?.Phone);
            const isBrizTvLogin = isIptvBrizTvLogin || isUnionBrizTvLogin;
            const serviceTypeID = item.Service?.Type?.id || item.Service?.id;
            const transitionToBrizTVServiceID = item.Service.TransitionToBrizTVServiceID;
            const hidden = item.Service.Hidden;
            const transitionToBrizTVService = servicePreset.Services?.find((i) => i.id === transitionToBrizTVServiceID);

            if (item.Service.Name)
              setConnectedServices((prevState) => [
                ...prevState,
                {
                  id: item.id,
                  type: el[0],
                  title: item.Service.Name,
                  serviceAnalyticsName: item.Service.AnalyticsID ?? item.Service.Name,
                  price: item.Service.Price,
                  shareAction:
                    item.Service.Share ??
                    (servicePreset?.PreviousPayedService?.Share?.CanSuspend
                      ? servicePreset?.PreviousPayedService?.Share
                      : null),
                  isPriceDaily: item.Service.Daily,
                  expireDate: item.RealExpireDate,
                  status: item.Status,
                  statusId: item.StatusID,
                  isDisabled: item.IsDisabled,
                  loyaltyProgram: item.LoyaltyProgram,
                  serviceExIds: servicePreset.ServiceExIDs,
                  isUnionCtvClassic,
                  isBrizTvLogin,
                  serviceTypeID,
                  isServiceActivationAvailable,
                  servicePreset,
                  isSuspense,
                  currentServiceId: item.Service.id,
                  isInitLoading: isLoading,
                  Hidden: hidden,
                  transitionToBrizTVServiceId: transitionToBrizTVServiceID,
                  transitionToBrizTVService,
                  ...(isInternetAccount && { login: item.Access.Login }),
                  ...(isUnionAccount && { login: item.Internet?.Access.Login }),
                  ...(isInternetDedicatedIP && { dedicatedIP: item.DedicatedIP }),
                  ...(isUnionDedicatedIP && { dedicatedIP: item.Internet?.DedicatedIP }),
                  ...(isBrizTvLogin && { isBrizTvShare: isBrizTvShareActive }),
                  ...(isBrizTvLogin && brizTvShareName && { brizTvShareName }),
                  ...(isBrizTvLogin && brizTvShareActivateTo && { brizTvShareActiveTo: brizTvShareActivateTo }),
                },
              ]);
          })
        );
      });
    }
  }, [connectedAccounts, userInfoState, isLoadingConnectedAccounts, brizTvShare, servicePresetsAll]);

  return (
    <div className="service-cards-container">
      {!controlledAccount &&
        repairWorksInfo?.filter(isRepairWorkStarted).map((el) => {
          return <TechnicalWorks key={el.id} work={el} />;
        })}
      {!controlledAccount ? (
        <>
          {!showServiceCardsSkeleton && !!bannersInfo?.length ? (
            <div className="service-cards-container__cards-container" style={{ marginBottom: '33px' }}>
              <NewOffersPromo />
            </div>
          ) : null}
          <div className="service-cards-container__header">
            {userAddress ? (
              <strong className="home-title">
                {t('home.yourServices')} ({userAddress?.trim()})
              </strong>
            ) : (
              <strong className="home-title">{t('home.yourServices')}</strong>
            )}
            {accountInfo?.Account?.Role === 'admin' &&
              accountInfo.List.filter((el) => el.Status === 'verified' && el.UserID !== accountInfo?.Account?.UserID)
                .length > 0 && (
                <TextButton
                  label={t('buttons.selectAnotherAccount')}
                  handleClick={handleSelectAnotherAccount}
                  className={TextButtonClassNames.PrimaryActive}
                  isArrow
                />
              )}
          </div>
        </>
      ) : null}
      <div className="service-cards-container__cards-container">
        {showServiceCardsSkeleton ? (
          <>
            <ServiceCardSkeleton />
            <ServiceCardSkeleton />
            <ServiceCardSkeleton />
          </>
        ) : (
          <>
            {isShownGigabitSpeedPromo && <GigabitSpeedPromo />}
            {controlledAccount ? (
              <ManageAccount
                address={userAddress.trim()}
                user={accountInfo?.List.find((el) => el.UserID === JSON.parse(controlledAccount).UserID)}
              />
            ) : null}
          </>
        )}
        {connectedServices.length
          ? connectedServices.map((el) => {
              return (
                <Service
                  key={el.id}
                  {...el}
                  permissions={userInfoState?.Permissions || initialState.userInfoState.Permissions}
                  shares={userInfoState?.Shares || initialState.userInfoState.Shares}
                  userId={userInfoState?.id || initialState.userInfoState.id}
                  userAddresses={userInfoState?.Addresses || initialState.userInfoState.Addresses}
                  userTypeID={userInfoState?.TypeID || initialState.userInfoState.TypeID}
                />
              );
            })
          : null}
      </div>
    </div>
  );
};
